/**
 * A minimal ZIP writer: stored entries, no compression.
 *
 * Written by hand rather than adding a dependency: every dependency is a
 * supply-chain surface in an app that holds clinical records. The format below
 * is the original PKZIP layout, which every unzip utility since 1989 reads.
 *
 * It was written for a CSV export that has since been removed, and its one
 * caller now is `xlsxWrite.ts` — an `.xlsx` is a ZIP of XML parts. Compression
 * would save a few kilobytes on a file opened once in Excel, and stored
 * entries keep this readable; leave it uncompressed unless a real logbook
 * starts producing workbooks big enough to care about.
 */

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;

  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 1 ? (crc >>> 1) ^ 0xedb88320 : crc >>> 1;
    }
  }

  return (crc ^ 0xffffffff) >>> 0;
}

type Chunk = { name: string; bytes: Uint8Array; crc: number; offset: number };

function writeUint32(view: DataView, offset: number, value: number): void {
  view.setUint32(offset, value, true);
}

function writeUint16(view: DataView, offset: number, value: number): void {
  view.setUint16(offset, value, true);
}

/**
 * `files` is name → text content. Returns a Blob ready to hand to a download.
 *
 * Timestamps are written as a fixed 1 January 1980, the epoch of the format
 * itself: the modification time of a file inside an export carries no
 * information anyone needs, and a real one would vary between two exports of
 * identical data.
 */
export function zip(files: Record<string, string>): Blob {
  const encoder = new TextEncoder();
  const chunks: Chunk[] = [];
  const parts: Uint8Array[] = [];
  let offset = 0;

  for (const [name, content] of Object.entries(files)) {
    const bytes = encoder.encode(content);
    const nameBytes = encoder.encode(name);
    const header = new Uint8Array(30 + nameBytes.length);
    const view = new DataView(header.buffer);

    writeUint32(view, 0, 0x04034b50); // local file header
    writeUint16(view, 4, 20); // version needed
    writeUint16(view, 6, 0); // flags
    writeUint16(view, 8, 0); // stored, not deflated
    writeUint16(view, 10, 0); // time
    writeUint16(view, 12, 33); // date: 1980-01-01
    writeUint32(view, 14, crc32(bytes));
    writeUint32(view, 18, bytes.length);
    writeUint32(view, 22, bytes.length);
    writeUint16(view, 26, nameBytes.length);
    writeUint16(view, 28, 0); // extra field length
    header.set(nameBytes, 30);

    chunks.push({ name, bytes, crc: crc32(bytes), offset });
    parts.push(header, bytes);
    offset += header.length + bytes.length;
  }

  const directoryStart = offset;

  for (const chunk of chunks) {
    const nameBytes = encoder.encode(chunk.name);
    const entry = new Uint8Array(46 + nameBytes.length);
    const view = new DataView(entry.buffer);

    writeUint32(view, 0, 0x02014b50); // central directory header
    writeUint16(view, 4, 20); // version made by
    writeUint16(view, 6, 20); // version needed
    writeUint16(view, 8, 0);
    writeUint16(view, 10, 0);
    writeUint16(view, 12, 0);
    writeUint16(view, 14, 33);
    writeUint32(view, 16, chunk.crc);
    writeUint32(view, 20, chunk.bytes.length);
    writeUint32(view, 24, chunk.bytes.length);
    writeUint16(view, 28, nameBytes.length);
    writeUint16(view, 30, 0);
    writeUint16(view, 32, 0);
    writeUint16(view, 34, 0);
    writeUint16(view, 36, 0);
    writeUint32(view, 38, 0);
    writeUint32(view, 42, chunk.offset);
    entry.set(nameBytes, 46);

    parts.push(entry);
    offset += entry.length;
  }

  const end = new Uint8Array(22);
  const endView = new DataView(end.buffer);
  writeUint32(endView, 0, 0x06054b50); // end of central directory
  writeUint16(endView, 8, chunks.length);
  writeUint16(endView, 10, chunks.length);
  writeUint32(endView, 12, offset - directoryStart);
  writeUint32(endView, 16, directoryStart);
  parts.push(end);

  return new Blob(parts as BlobPart[], { type: 'application/zip' });
}
